import React from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  TableCellsIcon,
  SparklesIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  ClockIcon,
  MapIcon,
  ArrowRightIcon,
  XMarkIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function RoleCalibrationHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-600 rounded-full mb-6">
            <UserGroupIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Role Calibration Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Define job positions, responsibilities, and compensation for your team
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-rose-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is Role Calibration?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is where you create and manage job positions in your company. Define what each role does, how much they get paid, their work schedule, and key responsibilities. It's like building the organizational chart that keeps your team structured and everyone knowing their place.
              </p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Process Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">Step-by-Step Role Creation Process</h2>

          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-rose-200 via-pink-200 to-rose-200 hidden md:block"></div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-rose-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-rose-600 font-bold text-xl">1</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Click Add Role</h3>
                  <p className="text-sm text-gray-600">Start by clicking the blue "Add Role" button at the top</p>
                  <div className="mt-3 p-3 bg-rose-50 rounded-lg border border-rose-200">
                    <PlusIcon className="w-6 h-6 text-rose-600 mx-auto mb-1" />
                    <p className="text-xs text-rose-700 font-medium">Add Role</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-rose-400" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-pink-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-pink-600 font-bold text-xl">2</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Basic Info</h3>
                  <p className="text-sm text-gray-600">Enter role name and description</p>
                  <div className="mt-3 space-y-2">
                    <div className="p-2 bg-pink-50 rounded border border-pink-200">
                      <div className="w-full h-2 bg-pink-200 rounded"></div>
                      <p className="text-xs text-pink-700 mt-1">Role Name</p>
                    </div>
                    <div className="p-2 bg-pink-50 rounded border border-pink-200">
                      <div className="w-full h-6 bg-pink-200 rounded"></div>
                      <p className="text-xs text-pink-700 mt-1">Description</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-pink-400" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-purple-600 font-bold text-xl">3</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">AI Generate Tasks</h3>
                  <p className="text-sm text-gray-600">Use AI to suggest responsibilities</p>
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <SparklesIcon className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                    <p className="text-xs text-purple-700 font-medium">AI Generate</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-purple-400" />
              </div>

              {/* Step 4 */}
              <div className="relative">
                <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-indigo-600 font-bold text-xl">4</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Set Details</h3>
                  <p className="text-sm text-gray-600">Configure pay, hours, and requirements</p>
                  <div className="mt-3 space-y-2">
                    <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
                      <CurrencyDollarIcon className="w-4 h-4 text-indigo-600 mx-auto mb-1" />
                      <p className="text-xs text-indigo-700">Pay Rate</p>
                    </div>
                    <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
                      <ClockIcon className="w-4 h-4 text-indigo-600 mx-auto mb-1" />
                      <p className="text-xs text-indigo-700">Hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Step */}
            <div className="mt-8 text-center">
              <div className="flex flex-wrap justify-center items-center space-x-2 md:space-x-4">
                <ArrowRightIcon className="w-6 h-6 text-indigo-400" />
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-green-600 font-bold text-xl">5</span>
                </div>
                <ArrowRightIcon className="w-6 h-6 text-green-400" />
              </div>
              <div className="mt-4">
                <h3 className="font-semibold text-gray-900 mb-2">Save & Complete</h3>
                <p className="text-sm text-gray-600">Click Save to add the role to your organization</p>
                <div className="mt-3 inline-flex items-center space-x-3">
                  <div className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">
                    <XMarkIcon className="w-4 h-4 inline mr-1" />
                    Cancel
                  </div>
                  <div className="px-4 py-2 bg-green-500 text-white rounded-lg">
                    <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                    Save
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page Layout Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Page Layout</h2>

          {/* Desktop Layout */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <ComputerDesktopIcon className="w-5 h-5 mr-2 text-rose-600" />
              Desktop View
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300">
              <div className="space-y-4">
                {/* Add Role Button */}
                <div className="flex justify-end">
                  <div className="w-32 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <PlusIcon className="w-4 h-4 text-white mr-2" />
                    <span className="text-white text-sm font-medium">Add Role</span>
                  </div>
                </div>

                {/* Roles Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-700">
                      <div className="col-span-2">Name</div>
                      <div className="col-span-3">Description</div>
                      <div className="col-span-2">Contract</div>
                      <div className="col-span-1">Pay</div>
                      <div className="col-span-1">Staff</div>
                      <div className="col-span-3">Actions</div>
                    </div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-gray-200">
                    <div className="px-4 py-3">
                      <div className="grid grid-cols-12 gap-4 items-center text-sm">
                        <div className="col-span-2 font-medium text-gray-900">Manager</div>
                        <div className="col-span-3 text-gray-600">Oversee team operations</div>
                        <div className="col-span-2 text-gray-600">Full Time</div>
                        <div className="col-span-1 text-gray-600">$25/hr</div>
                        <div className="col-span-1 text-gray-600">3</div>
                        <div className="col-span-3 flex space-x-2">
                          <button className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Add Task</button>
                          <button className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            <PencilIcon className="w-3 h-3 inline mr-1" />
                            Edit
                          </button>
                          <button className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                            <TrashIcon className="w-3 h-3 inline mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <DevicePhoneMobileIcon className="w-5 h-5 mr-2 text-pink-600" />
              Mobile View
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 max-w-xs mx-auto">
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="w-24 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <PlusIcon className="w-3 h-3 text-white mr-1" />
                    <span className="text-white text-xs">Add</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="font-medium text-gray-900 text-sm mb-1">Manager</div>
                    <div className="text-xs text-gray-600 mb-2">Full Time • $25/hr • 3 staff</div>
                    <div className="flex space-x-1">
                      <div className="w-6 h-6 bg-green-100 rounded"></div>
                      <div className="w-6 h-6 bg-blue-100 rounded"></div>
                      <div className="w-6 h-6 bg-red-100 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
              <SparklesIcon className="w-6 h-6 text-rose-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">AI Task Generation</h3>
            <p className="text-gray-600 text-sm md:text-base">Get instant responsibility suggestions based on role descriptions</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
              <CurrencyDollarIcon className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Flexible Compensation</h3>
            <p className="text-gray-600 text-sm md:text-base">Set hourly or annual pay rates with easy editing capabilities</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <ClockIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Contract Types</h3>
            <p className="text-gray-600 text-sm md:text-base">Define 0 Hours, Part Time, or Full Time positions with custom schedules</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <MapIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Travel Requirements</h3>
            <p className="text-gray-600 text-sm md:text-base">Specify travel needs for expense tracking and planning</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Staff Tracking</h3>
            <p className="text-gray-600 text-sm md:text-base">See how many people are assigned to each role at a glance</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
              <CheckCircleIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Auto-Calculations</h3>
            <p className="text-gray-600 text-sm md:text-base">Break lengths automatically suggested based on working hours</p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-2xl p-6 md:p-8 text-white mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Why Role Calibration Matters</h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Clear Structure</h3>
                <p className="text-rose-100">Everyone knows their responsibilities and expectations</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Better Hiring</h3>
                <p className="text-rose-100">Defined roles make recruitment and onboarding smoother</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Cost Management</h3>
                <p className="text-rose-100">Track pay rates and staffing levels for budgeting</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Legal Compliance</h3>
                <p className="text-rose-100">Ensure fair contracts and proper break allocations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <SparklesIcon className="w-6 h-6 mr-3 text-rose-500" />
            How to Create a Role
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Start with Basics</h3>
                <p className="text-gray-600">Click "Add Role" and enter the job title and description</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Generate Tasks</h3>
                <p className="text-gray-600">Use the AI button to get responsibility suggestions, or add them manually</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Set Compensation</h3>
                <p className="text-gray-600">Enter pay rate and choose hourly or annual compensation</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Define Schedule</h3>
                <p className="text-gray-600">Choose contract type and set working days/hours</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-rose-600 font-bold text-sm">5</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Add Details</h3>
                <p className="text-gray-600">Set break length and travel requirements, then save</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <EyeIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Manage Existing Roles</h3>
                <p className="text-blue-700 text-sm">
                  Use the table to add more tasks, edit pay rates (admins only), or delete roles.
                  Always check staff assignments before deleting roles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}